import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, RefreshCw, ArrowRight } from 'lucide-react';
import ChangePasswordExpiredDialog from '../components/ChangePasswordExpiredDialog';
import api from '../utils/api';
import './Login.blueprint.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_METADATA = {
  version: 'v4.2 · IBC/2026.05',
  entorno: 'Producción',
  autor: 'Desarrollado por el equipo IBC',
};

/** Fondo Blueprint: grid arquitectónico animado. */
const BlueprintBackground = () => (
  <div className="bp-bg" aria-hidden="true">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 2400 900"
      preserveAspectRatio="xMidYMid slice"
      aria-label="blueprint-grid"
    >
      <defs>
        <pattern id="bpGridSm" width="24" height="24" patternUnits="userSpaceOnUse">
          <path d="M 24 0 L 0 0 0 24" fill="none" stroke="#c5b8a2" strokeWidth="0.4" opacity="0.35" />
        </pattern>
        <pattern id="bpGridLg" width="120" height="120" patternUnits="userSpaceOnUse">
          <rect width="120" height="120" fill="url(#bpGridSm)" />
          <path d="M 120 0 L 0 0 0 120" fill="none" stroke="#c5b8a2" strokeWidth="0.9" opacity="0.5" />
        </pattern>
      </defs>
      <rect width="2400" height="900" fill="url(#bpGridLg)" />
      <g stroke="#c5b8a2" strokeWidth="0.8" opacity="0.25" fill="none">
        <path d="M 0 200 C 400 180 800 260 1200 200 S 2000 180 2400 220" />
        <path d="M 0 400 C 400 380 800 460 1200 400 S 2000 380 2400 420" />
        <path d="M 0 600 C 400 580 800 660 1200 600 S 2000 580 2400 620" />
      </g>
      <g stroke="#9d8272" strokeWidth="1" opacity="0.18" fill="none">
        <circle cx="300" cy="300" r="180" />
        <circle cx="1600" cy="600" r="140" />
        <rect x="900" y="150" width="220" height="220" />
        <line x1="900" y1="150" x2="1120" y2="370" />
      </g>
    </svg>
  </div>
);

/** Marco con 4 corner ticks. */
const CornerTicks = () => (
  <>
    <span className="corner-tick tick-tl" />
    <span className="corner-tick tick-tr" />
    <span className="corner-tick tick-bl" />
    <span className="corner-tick tick-br" />
  </>
);

/** Shell común (header + background + card wrapper + footer).
 * DEFINIDO A NIVEL DE MÓDULO para que su referencia sea estable entre renders
 * de <Login/>. Si estuviera dentro del componente funcional Login, React lo
 * trataría como un componente nuevo en cada render → unmount/remount →
 * la animación CSS .fade-in se dispararía cada vez (produce 3 parpadeos
 * al cargar: render inicial + llegada de config + llegada de metadata).
 */
const Shell = ({
  cardLabel,
  cardContent,
  testId,
  config,
  metadata,
  currentYear,
  showForgotLink,
}) => (
  <div
    className="login-bp min-h-screen flex items-center justify-center bg-[#E8E8E7] relative overflow-hidden p-4 sm:p-6"
    data-testid={testId}
  >
    <BlueprintBackground />

    <div className="relative z-10 w-full max-w-md fade-in">
      {/* Header */}
      <div className="text-center mb-7 sm:mb-8">
        {/* Contenedor con altura fija reservada — evita salto visual cuando
             llega config.logo_url asíncronamente. El <img> hace fade-in
             dentro del espacio ya reservado. */}
        <div className="h-14 sm:h-16 mb-3 flex items-center justify-center">
          {config.logo_url && (
            <img
              src={config.logo_url}
              alt="Logo"
              className="max-h-full w-auto max-w-[200px] object-contain transition-opacity duration-300"
              data-testid="login-logo"
            />
          )}
        </div>
        <h1
          className="text-4xl sm:text-5xl font-bold text-[#1A1614] leading-tight"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          {config.platform_name || 'Business Central'}
        </h1>
        <p className="mt-3 text-[13px] text-[#5A5A5A] max-w-sm mx-auto leading-relaxed">
          Plataforma de automatización de procesos inmobiliarios del Grupo IMAGINA.
        </p>
      </div>

      {/* Card */}
      <div className="card-blueprint p-7 sm:p-10">
        <CornerTicks />

        <div className="flex items-center justify-between mb-7">
          <div>
            <p className="label-tech">{cardLabel}</p>
            <p className="mono mt-1">{metadata.version}</p>
          </div>
          <div className="text-right">
            <p className="label-tech">Entorno</p>
            <p className="mono mt-1">{metadata.entorno}</p>
          </div>
        </div>

        {cardContent}

        <div className="mt-7 pt-5 border-t border-dashed flex items-center justify-between" style={{ borderColor: 'color-mix(in srgb, var(--skin-ink-mute) 60%, transparent)' }}>
          <span className="label-tech">© Grupo IMAGINA {currentYear}</span>
          {showForgotLink && (
            <a href="#" className="link-soft">¿Olvidaste tu contraseña?</a>
          )}
        </div>
      </div>

      {/* Footer global — estructura en 2 líneas fijas para evitar reflow
          cuando llega metadata.autor (texto variable de longitud). */}
      <div
        className="mt-5 flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[#5A5A5A] text-center"
        style={{ fontFamily: 'Manrope, sans-serif' }}
        data-testid="login-footer-meta"
      >
        <span className="leading-snug">{metadata.autor}</span>
        <span className="leading-snug">Lima · PE</span>
      </div>
    </div>
  </div>
);

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordExpired, setPasswordExpired] = useState(false);
  const [expiredUserInfo, setExpiredUserInfo] = useState(null);
  const [config, setConfig] = useState({ logo_url: null, platform_name: 'Business Central' });
  const [metadata, setMetadata] = useState(DEFAULT_METADATA);

  // 2FA States
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAUserId, setTwoFAUserId] = useState(null);
  const [twoFAUserEmail, setTwoFAUserEmail] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [resending2FA, setResending2FA] = useState(false);

  const { login, setAuth } = useAuth();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    api.get('/configuracion/general').then(res => {
      if (res.data) setConfig(res.data);
    }).catch(() => {});

    // Endpoint público, sin token. Defaults se mantienen si falla.
    axios.get(`${BACKEND_URL}/api/public/login-metadata`)
      .then(res => setMetadata({ ...DEFAULT_METADATA, ...res.data }))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);

      if (result?.requires_2fa) {
        setRequires2FA(true);
        setTwoFAUserId(result.user_id);
        setTwoFAUserEmail(result.user_email);
        toast.info(result.message || 'Código de verificación enviado a tu correo');
        return;
      }

      if (result?.password_expired) {
        setPasswordExpired(true);
        setExpiredUserInfo({
          userId: result.user_id,
          userEmail: result.user_email,
          expirationInfo: result.password_info
        });
        toast.error('Su contraseña ha expirado. Debe cambiarla para continuar.');
      } else {
        toast.success('¡Bienvenido!');
        if (result?.user?.password_warning) {
          setTimeout(() => {
            toast.warning(result.user.password_warning, { duration: 8000 });
          }, 1000);
        }
        navigate('/home');
      }
    } catch (error) {
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers?.['retry-after'] || 60;
        const message = error.response.data?.detail || `Demasiados intentos. Intente en ${retryAfter} segundos.`;
        toast.error(message, { duration: 5000 });
      } else {
        toast.error('Error usuario o contraseña inválidos. Si has olvidado tus datos comunícate con el Área de Tecnología.', {
          duration: 6000
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error('Ingrese el código de 6 dígitos');
      return;
    }
    setVerifying2FA(true);
    try {
      const response = await api.post('/auth/verify-2fa', {
        user_id: twoFAUserId,
        code: twoFACode
      });
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      setAuth({ isAuthenticated: true, user, token: access_token });
      toast.success('¡Bienvenido!');
      navigate('/home');
    } catch (error) {
      const message = error.response?.data?.detail || 'Código incorrecto';
      toast.error(message);
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleResend2FA = async () => {
    setResending2FA(true);
    try {
      await api.post('/auth/resend-2fa', { user_id: twoFAUserId });
      toast.success('Nuevo código enviado a tu correo');
      setTwoFACode('');
    } catch (error) {
      toast.error('Error al reenviar el código');
    } finally {
      setResending2FA(false);
    }
  };

  const handleCancel2FA = () => {
    setRequires2FA(false);
    setTwoFAUserId(null);
    setTwoFAUserEmail(null);
    setTwoFACode('');
  };

  const handlePasswordChangeSuccess = async ({ newPassword } = {}) => {
    const targetEmail = expiredUserInfo?.userEmail || email;

    // Cerrar dialog y limpiar estado del form de login
    setPasswordExpired(false);
    setExpiredUserInfo(null);
    setPassword('');

    if (!newPassword) {
      toast.success('Contraseña actualizada. Por favor, inicie sesión con su nueva contraseña.');
      return;
    }

    // Auto-login con la nueva contraseña para evitar reproceso y autofill del browser
    try {
      const result = await login(targetEmail, newPassword);

      if (result?.requires_2fa) {
        setRequires2FA(true);
        setTwoFAUserId(result.user_id);
        setTwoFAUserEmail(result.user_email);
        toast.info(result.message || 'Código de verificación enviado a tu correo');
        return;
      }

      toast.success('¡Bienvenido!');
      if (result?.user?.password_warning) {
        setTimeout(() => {
          toast.warning(result.user.password_warning, { duration: 8000 });
        }, 1000);
      }
      navigate('/home');
    } catch (err) {
      toast.success('Contraseña actualizada. Por favor, inicie sesión con su nueva contraseña.');
    }
  };

  // -----------------------------------------------------------------
  // Shell está definido a nivel de módulo (ver arriba) para evitar parpadeo
  // por unmount/remount en cada setState.
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // 2FA
  // -----------------------------------------------------------------
  if (requires2FA) {
    return (
      <Shell
        testId="twofa-page"
        cardLabel="Verificación · 2FA"
        config={config}
        metadata={metadata}
        currentYear={currentYear}
        showForgotLink={false}
        cardContent={(
          <>
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'color-mix(in srgb, var(--skin-accent) 10%, transparent)' }}>
                <ShieldCheck className="w-7 h-7" style={{ color: 'var(--skin-accent)' }} />
              </div>
            </div>
            <p className="text-center text-sm mb-5" style={{ color: 'var(--skin-ink-soft)' }}>
              Enviamos un código de 6 dígitos a{' '}
              <span className="font-semibold" style={{ color: 'var(--skin-ink)' }}>{twoFAUserEmail}</span>
            </p>

            <form onSubmit={handleVerify2FA} className="space-y-5" data-testid="twofa-form">
              <div>
                <label className="label-tech-dark block mb-2">Código de verificación</label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                  className="input-bp border-0 border-b rounded-none focus-visible:ring-0 focus-visible:border-b-2 px-0 text-center text-2xl tracking-[0.5em] font-mono h-14 bg-transparent"
                  placeholder="000000"
                  autoComplete="one-time-code"
                  autoFocus
                  data-testid="twofa-code-input"
                />
              </div>

              <button
                type="submit"
                disabled={verifying2FA || twoFACode.length !== 6}
                className="btn-earth"
                data-testid="twofa-verify-button"
              >
                {verifying2FA ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Verificando...</>
                ) : (
                  <>Verificar Código <ArrowRight size={14} /></>
                )}
              </button>

              <div className="flex items-center justify-between pt-1">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancel2FA}
                  className="text-xs uppercase tracking-widest"
                  style={{ color: 'var(--skin-ink-soft)' }}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleResend2FA}
                  disabled={resending2FA}
                  className="text-xs uppercase tracking-widest"
                  style={{ color: 'var(--skin-accent)' }}
                  data-testid="twofa-resend-button"
                >
                  {resending2FA ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Reenviar</>
                  )}
                </Button>
              </div>
            </form>

            <p className="mt-5 text-[11px] text-center" style={{ color: 'var(--skin-ink-soft)' }}>
              El código expira en 10 minutos. Revisa tu bandeja de entrada y carpeta de spam.
            </p>
          </>
        )}
      />
    );
  }

  // -----------------------------------------------------------------
  // Login principal
  // -----------------------------------------------------------------
  return (
    <>
      <Shell
        testId="login-page"
        cardLabel="Acceso · Sesión"
        config={config}
        metadata={metadata}
        currentYear={currentYear}
        showForgotLink={true}
        cardContent={(
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <label htmlFor="email" className="label-tech-dark block mb-1">01 — Correo electrónico</label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input-bp border-0 border-b rounded-none focus-visible:ring-0 focus-visible:border-b-2 px-0 bg-transparent h-auto"
                placeholder="usuario@imagina.pe"
                data-testid="email-input"
              />
            </div>

            <div>
              <label htmlFor="password" className="label-tech-dark block mb-1">02 — Contraseña</label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input-bp border-0 border-b rounded-none focus-visible:ring-0 focus-visible:border-b-2 px-0 bg-transparent h-auto"
                placeholder="••••••••••"
                data-testid="password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-earth mt-2"
              data-testid="login-submit-button"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Iniciando sesión...</>
              ) : (
                <>Iniciar Sesión <ArrowRight size={14} /></>
              )}
            </button>
          </form>
        )}
      />

      {/* Dialog contraseña expirada (se superpone al layout Blueprint) */}
      {passwordExpired && expiredUserInfo && (
        <ChangePasswordExpiredDialog
          open={passwordExpired}
          userId={expiredUserInfo.userId}
          userEmail={expiredUserInfo.userEmail}
          expirationInfo={expiredUserInfo.expirationInfo}
          onSuccess={handlePasswordChangeSuccess}
        />
      )}
    </>
  );
};

export default Login;
